import getSubdomain from './getSubdomain'
import { medicareApiClient } from 'clients/medicareApiClient'
import { ChatTokenResponse } from 'models/Chat'
import { Logger } from './logger'

export class PopupBlockedError extends Error {
  constructor () {
    super('Live chat window failed to open')
  }
}

export interface LiveChatOptions {
  isAuthenticated: boolean;
  asPopup: boolean;
  viewpage?: string;
  lang?: 'en' | 'es';
}

export const openInterimLiveChat = async ({ isAuthenticated, asPopup, viewpage, lang }: LiveChatOptions) => {
  const mymedicareSubdomain: string = getSubdomain(process.env.REACT_APP_MY_MEDICARE_ENV || '')

  const chattype: string = isAuthenticated ? 'authenticated' : 'unauthenticated'
  const viewpageParam = viewpage ? '&viewpage=' + viewpage : ''
  const langParam = lang === 'es' ? '&lang=es-mx' : ''

  // Open window before async call; we will set the URL using fetched data
  const liveChatWindow = openEmptyLiveChatWindow(asPopup)

  if (!liveChatWindow) {
    return Promise.reject(new PopupBlockedError())
  }

  let ngdToken = ''
  try {
    ngdToken = await medicareApiClient.asyncGetToken()
  } catch (error) {
    console.error(error)
  }

  // Now that we have the token, we can set the URL
  const liveChatURL: string = `https://${mymedicareSubdomain}medicare.gov/mbp/webchatlogin.aspx?chattype=${chattype}&maxtoken=${ngdToken}${viewpageParam}${langParam}`
  liveChatWindow.location.href = liveChatURL
}

const openLiveChat = async ({ isAuthenticated, asPopup, viewpage = undefined, lang }: LiveChatOptions) => {
  let userInfo

  const liveChatUrl = process.env.REACT_APP_LIVE_CHAT_URL || 'https://chat.mymedicare.gov/chatclient/chatrequest.aspx'
  const chatUrl: URL = new URL(liveChatUrl)

  const liveChatWindow = openEmptyLiveChatWindow(asPopup)

  if (!liveChatWindow) {
    return Promise.reject(new PopupBlockedError())
  }

  if (!viewpage) {
    const h1s = document.getElementsByTagName('h1')
    if (h1s.length > 0) {
      viewpage = h1s.item(0)?.innerText
    }
  }

  // Right now we only really expect the word create to be used on Create account, or as an initial term, so it would always be capitalized.
  // Because of this we capitalized Register
  viewpage = viewpage?.replaceAll(/create/ig, 'Register')
  viewpage = viewpage?.replaceAll(/delete/ig, 'Deactivate')

  // TODO - deprecate this if/else logic and just keep it to append('language')
  if (isAuthenticated) {
    try {
      userInfo = await medicareApiClient.asyncEncryptChatInfo({ language: lang || 'en', currentView: `${viewpage?.substring(0, 150) || 'Default'}` })
    } catch (error) {
      Logger.error(error)
      // TODO: Determine possible UX if we get error from Core API (e.g. viewpage is not supported, etc)
      liveChatWindow.close()
      return
    }

    postUserInfoLiveChatWindow({ userInfo, action: chatUrl.href })
  } else {
    // TODO - Refactor this code once we work on auth

    const langParam = lang === 'es' ? 'es-mx' : 'en-us'
    chatUrl.searchParams.append('language', langParam)
    chatUrl.searchParams.append('currentView', viewpage?.substring(0, 150) || 'Default')

    setLiveChatWindowUrl(liveChatWindow, chatUrl.href)
  }
}

export default openLiveChat

// We separate opening the window from setting the URL due to an issue w/ Safari & Firefox
const openEmptyLiveChatWindow = (asPopup: boolean) => {
  return window.open(undefined, 'LiveChat', asPopup ? 'height=730,width=680,scrollbars=1,left=50,top=100' : undefined)
}

const setLiveChatWindowUrl = (liveChatWindow: Window | null, url: string) => {
  if (!liveChatWindow) return
  liveChatWindow.location.href = url
}

const postUserInfoLiveChatWindow = ({ userInfo, action }: {userInfo: ChatTokenResponse, action: string}) => {
  const windowName = 'LiveChat'

  const form = document.createElement('form')
  form.setAttribute('method', 'post')
  form.setAttribute('action', action)

  form.setAttribute('target', windowName)

  Object.entries(userInfo).forEach(([key, value]) => {
    const hiddenField = document.createElement('input')
    hiddenField.setAttribute('type', 'hidden')
    hiddenField.setAttribute('name', key)
    hiddenField.setAttribute('value', value)
    form.appendChild(hiddenField)
  })

  document.body.appendChild(form)

  // Submit the form to the popup window
  form.submit()

  // Remove the form from the page
  form.remove()
}
